import { useMemo, useState } from "react";
import type { Folder, FolderDetails } from "src/components/Folders/types";
import {
	useCreateFolder,
	useFoldersSearchInfinite,
} from "src/hooks/useFolders";

interface UseFolderPickerOptions {
	initialSelectedIds?: string[];
	multiSelect?: boolean;
	excludeFolderId?: string;
}

interface UseFolderPickerReturn {
	selectedFolderIds: string[];
	folders: Folder[];
	isLoading: boolean;
	showCreateModal: boolean;
	isCreatingFolder: boolean;
	setShowCreateModal: (show: boolean) => void;
	toggleFolderSelection: (id: string) => void;
	handleCreateFolder: (
		name: string,
		details: FolderDetails | null,
	) => Promise<void>;
}

export function useFolderPicker({
	initialSelectedIds = [],
	multiSelect = false,
	excludeFolderId,
}: UseFolderPickerOptions = {}): UseFolderPickerReturn {
	const [selectedFolderIds, setSelectedFolderIds] =
		useState<string[]>(initialSelectedIds);
	const [showCreateModal, setShowCreateModal] = useState(false);

	const { data, isLoading } = useFoldersSearchInfinite({ take: 50 });
	const { mutateAsync: createFolder, isPending: isCreatingFolder } =
		useCreateFolder();

	const folders = useMemo(() => {
		if (!data?.pages) return [];
		return data.pages
			.flatMap((page) => page.data || [])
			.filter(
				(f) => !excludeFolderId || String(f.id) !== String(excludeFolderId),
			);
	}, [data?.pages, excludeFolderId]);

	const toggleFolderSelection = (id: string) => {
		if (multiSelect) {
			setSelectedFolderIds((prev) =>
				prev.includes(id) ? prev.filter((fId) => fId !== id) : [...prev, id],
			);
		} else {
			setSelectedFolderIds([id]);
		}
	};

	const handleCreateFolder = async (
		name: string,
		details: FolderDetails | null,
	) => {
		const { id } = await createFolder({ name, details });
		const idStr = String(id);
		setSelectedFolderIds((prev) => (multiSelect ? [...prev, idStr] : [idStr]));
	};

	return {
		selectedFolderIds,
		folders,
		isLoading,
		showCreateModal,
		isCreatingFolder,
		setShowCreateModal,
		toggleFolderSelection,
		handleCreateFolder,
	};
}
